import React, { useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  RotateCcw,
  Trophy,
  X,
} from "lucide-react";
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls";
import useStudentPortalFetch, {
  StudentPortalShell,
  StudentPortalState,
} from "./useStudentPortalFetch";

const OPTION_LETTERS = ["A", "B", "C", "D"];

function initials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "Q";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function marksLabel(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "1 mark";
  return n === 1 ? "1 mark" : `${n} marks`;
}

export default function StudentQuestionsPage() {
  const { data, loading, error } = useStudentPortalFetch("questions");
  const programs = Array.isArray(data?.programs) ? data.programs : [];

  const [programId, setProgramId] = useState(null);
  const [moduleId, setModuleId] = useState("all");
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [checkingId, setCheckingId] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const selectedProgram = useMemo(
    () =>
      programs.find(
        (row) =>
          String(row.organization_ems_training_program_id) === String(programId)
      ) || null,
    [programs, programId]
  );

  const quizQuestions = useMemo(() => {
    if (!selectedProgram) return [];
    const modules = Array.isArray(selectedProgram.modules)
      ? selectedProgram.modules
      : [];
    const scoped =
      moduleId === "all"
        ? modules
        : modules.filter(
            (row) =>
              String(row.organization_ems_training_program_module_id) ===
              String(moduleId)
          );

    return scoped.flatMap((mod) =>
      (mod.questions || []).map((question, questionIndex) => ({
        ...question,
        module_name: mod.module_name,
        module_id: mod.organization_ems_training_program_module_id,
        number: questionIndex + 1,
      }))
    );
  }, [selectedProgram, moduleId]);

  const current = quizQuestions[index] || null;
  const answeredCount = quizQuestions.filter(
    (q) => answers[q.organization_ems_module_question_id]
  ).length;
  const correctCount = quizQuestions.filter(
    (q) => answers[q.organization_ems_module_question_id]?.is_correct
  ).length;
  const earnedMarks = quizQuestions.reduce((sum, q) => {
    const result = answers[q.organization_ems_module_question_id];
    return sum + Number(result?.marks_awarded || 0);
  }, 0);
  const totalMarks = quizQuestions.reduce(
    (sum, q) => sum + Number(q.marks || 0),
    0
  );
  const progressPct = quizQuestions.length
    ? Math.round((answeredCount / quizQuestions.length) * 100)
    : 0;

  const startProgram = (id) => {
    setProgramId(id);
    setModuleId("all");
    setIndex(0);
    setAnswers({});
    setShowResults(false);
  };

  const changeModule = (nextModuleId) => {
    setModuleId(nextModuleId);
    setIndex(0);
    setAnswers({});
    setShowResults(false);
  };

  const resetQuiz = () => {
    setIndex(0);
    setAnswers({});
    setShowResults(false);
  };

  const checkOption = async (question, optionKey) => {
    const questionId = question.organization_ems_module_question_id;
    if (!questionId || answers[questionId] || checkingId) return;

    setCheckingId(questionId);
    try {
      const res = await axios.post(
        `${MAIN_URL}/api/ems/portal/questions/${questionId}/check`,
        { selected: optionKey },
        { headers: getApiHeaders() }
      );
      const payload = res?.data?.data || {};
      setAnswers((prev) => ({
        ...prev,
        [questionId]: {
          selected: payload.selected || optionKey,
          is_correct: Boolean(payload.is_correct),
          correct_answer: payload.correct_answer,
          marks_awarded: Number(payload.marks_awarded || 0),
        },
      }));
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Could not check that answer."
      );
    } finally {
      setCheckingId(null);
    }
  };

  const goNext = () => {
    if (index >= quizQuestions.length - 1) {
      setShowResults(true);
      return;
    }
    setIndex((value) => value + 1);
  };

  if (!programId) {
    return (
      <StudentPortalShell
        className="ems-quiz-page"
        title="Practice Questions"
        subtitle="Attempt questions from your enrolled courses. Pick an option to see if you got it right."
      >
        <StudentPortalState
          loading={loading}
          error={error}
          empty={!loading && !error && programs.length === 0}
          emptyText="Practice from the question bank is not available here. Use My Assessments for scheduled tests."
        >
          <section className="ems-student-section">
            <div className="ems-student-cards ems-course-cards">
              {programs.map((program) => (
                <article
                  key={program.organization_ems_training_program_id}
                  className="ems-student-card ems-course-card ems-quiz-program-card"
                >
                  <div className="ems-course-card-accent" />
                  <div className="ems-course-card-body">
                    <div className="ems-course-card-top">
                      <div className="ems-course-card-icon" aria-hidden="true">
                        {initials(program.training_program_name)}
                      </div>
                      <div className="ems-course-card-heading">
                        <h3>{program.training_program_name}</h3>
                        <p>
                          {program.category_name ||
                            program.training_program_code ||
                            "Course questions"}
                        </p>
                      </div>
                    </div>
                    <div className="ems-course-card-meta">
                      <div>
                        <span>Questions</span>
                        <b>{program.question_count || 0}</b>
                      </div>
                      <div>
                        <span>Total marks</span>
                        <b>{program.total_marks || 0}</b>
                      </div>
                      <div>
                        <span>Modules</span>
                        <b>{program.modules?.length || 0}</b>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="ems-student-btn ems-student-btn-primary ems-quiz-start-btn"
                      onClick={() =>
                        startProgram(
                          program.organization_ems_training_program_id
                        )
                      }
                    >
                      <CircleHelp size={16} strokeWidth={2.2} />
                      Start practice
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </StudentPortalState>
      </StudentPortalShell>
    );
  }

  return (
    <StudentPortalShell
      className="ems-quiz-page"
      title={selectedProgram?.training_program_name || "Practice Questions"}
      subtitle="Choose a module, pick an answer, and move through the set at your own pace."
      headerRight={
        <button
          type="button"
          className="ems-student-btn ems-student-btn-secondary"
          onClick={() => {
            setProgramId(null);
            resetQuiz();
          }}
        >
          All courses
        </button>
      }
    >
      <div className="ems-quiz-modules" role="tablist">
        <button
          type="button"
          className={`ems-catalog-tab${moduleId === "all" ? " is-active" : ""}`}
          onClick={() => changeModule("all")}
        >
          All modules
        </button>
        {(selectedProgram?.modules || []).map((mod) => (
          <button
            key={mod.organization_ems_training_program_module_id}
            type="button"
            className={`ems-catalog-tab${
              String(moduleId) ===
              String(mod.organization_ems_training_program_module_id)
                ? " is-active"
                : ""
            }`}
            onClick={() =>
              changeModule(mod.organization_ems_training_program_module_id)
            }
          >
            {mod.module_name}
            <span>{mod.question_count}</span>
          </button>
        ))}
      </div>

      {quizQuestions.length === 0 ? (
        <div className="ems-student-portal-state">
          No questions in this module yet.
        </div>
      ) : showResults ? (
        <section className="ems-quiz-results">
          <div className="ems-quiz-score-ring" aria-hidden="true">
            <Trophy size={28} strokeWidth={2} />
          </div>
          <h2>Practice complete</h2>
          <p>
            You scored {correctCount} of {quizQuestions.length} questions
            correctly
            {totalMarks ? ` · ${earnedMarks} / ${totalMarks} marks` : ""}.
          </p>
          <div className="ems-quiz-result-stats">
            <div>
              <span>Correct</span>
              <b>{correctCount}</b>
            </div>
            <div>
              <span>Incorrect</span>
              <b>{answeredCount - correctCount}</b>
            </div>
            <div>
              <span>Attempted</span>
              <b>
                {answeredCount}/{quizQuestions.length}
              </b>
            </div>
          </div>
          <div className="ems-quiz-nav">
            <button
              type="button"
              className="ems-student-btn ems-student-btn-secondary"
              onClick={resetQuiz}
            >
              <RotateCcw size={16} />
              Try again
            </button>
            <button
              type="button"
              className="ems-student-btn ems-student-btn-primary"
              onClick={() => {
                setProgramId(null);
                resetQuiz();
              }}
            >
              Back to courses
            </button>
          </div>
        </section>
      ) : (
        <section className="ems-quiz-board">
          <div className="ems-quiz-progress">
            <div className="ems-quiz-progress-copy">
              <strong>
                Question {index + 1} of {quizQuestions.length}
              </strong>
              <span>
                {answeredCount} answered · {correctCount} correct
              </span>
            </div>
            <div className="ems-quiz-progress-bar" aria-hidden="true">
              <span style={{ width: `${progressPct}%` }} />
            </div>
          </div>

          {current ? (
            <article className="ems-quiz-card">
              <div className="ems-quiz-card-meta">
                <span className="ems-quiz-module-chip">
                  {current.module_name || "Module"}
                </span>
                <span className="ems-quiz-marks-chip">
                  {marksLabel(current.marks)}
                </span>
              </div>
              <h2 className="ems-quiz-question">{current.question}</h2>
              <div className="ems-quiz-options">
                {(current.options || []).map((option, optionIndex) => {
                  const result =
                    answers[current.organization_ems_module_question_id];
                  const selected = result?.selected === option.key;
                  const isCorrectOption =
                    result && result.correct_answer === option.key;
                  const isWrongSelected = selected && result && !result.is_correct;
                  const busy =
                    checkingId === current.organization_ems_module_question_id;

                  return (
                    <button
                      key={option.key}
                      type="button"
                      disabled={Boolean(result) || busy}
                      className={[
                        "ems-quiz-option",
                        selected ? "is-selected" : "",
                        isCorrectOption ? "is-correct" : "",
                        isWrongSelected ? "is-wrong" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => checkOption(current, option.key)}
                    >
                      <span className="ems-quiz-option-letter">
                        {option.label || OPTION_LETTERS[optionIndex] || "?"}
                      </span>
                      <span className="ems-quiz-option-text">{option.text}</span>
                      {isCorrectOption ? <Check size={18} /> : null}
                      {isWrongSelected ? <X size={18} /> : null}
                    </button>
                  );
                })}
              </div>

              {answers[current.organization_ems_module_question_id] ? (
                <div
                  className={`ems-quiz-feedback ${
                    answers[current.organization_ems_module_question_id]
                      .is_correct
                      ? "is-correct"
                      : "is-wrong"
                  }`}
                >
                  {answers[current.organization_ems_module_question_id]
                    .is_correct
                    ? "Correct. Nice work."
                    : "Not quite. The highlighted option is the right answer."}
                </div>
              ) : null}
            </article>
          ) : null}

          <div className="ems-quiz-nav">
            <button
              type="button"
              className="ems-student-btn ems-student-btn-secondary"
              disabled={index === 0}
              onClick={() => setIndex((value) => Math.max(0, value - 1))}
            >
              <ChevronLeft size={16} />
              Previous
            </button>
            <button
              type="button"
              className="ems-student-btn ems-student-btn-primary"
              onClick={goNext}
            >
              {index >= quizQuestions.length - 1 ? "See results" : "Next"}
              <ChevronRight size={16} />
            </button>
          </div>
        </section>
      )}
    </StudentPortalShell>
  );
}
